<template>
  <div :class="['completion-view', { 'completion-view--updated': updated }]">
    <div
      v-if="!updated"
      class="completion-view__illustration"
    >
      <BikeSetupFinished />
    </div>

    <div class="completion-view__content">
      <TitleBar
        v-if="!updated"
        :title="t('steps.completion')"
      >
        <BfButton
          :type="ButtonType.Outlined"
          icon="/icons/52-Arrows-Diagrams/01-Arrows/arrow-left-1.svg"
          @click="previous"
        >
          {{ t('common.previous') }}
        </BfButton>
        <BfButton @click="next">
          {{ t('common.finish') }}
        </BfButton>
      </TitleBar>

      <FinishedSetup v-if="!updated" />
      <div v-else>
        <h1>{{ t('completion.updateXY.success.title') }}</h1>
        <p>{{ t('completion.updateXY.success.content') }}</p>
        <BfButton @click="backToPortal">{{
          t('completion.updateXY.backToPortal')
        }}</BfButton>
      </div>
    </div>
  </div>

  <n-modal
    v-model:show="showModal"
    style="width: 600px"
    preset="card"
    :title="t('completion.confirmation.title')"
    :bordered="false"
    size="huge"
    transform-origin="center"
  >
    <p>{{ t('completion.confirmation.line1') }}</p>
    <p>{{ t('completion.confirmation.line2') }}</p>
    <p>{{ t('completion.confirmation.line3') }}</p>

    <template #action>
      <div class="completion-view__actions">
        <BfButton
          :type="ButtonType.Outlined"
          @click="showModal = false"
        >
          {{ t('common.cancel') }}
        </BfButton>
        <BfButton
          :state="ButtonState.Enabled"
          :type="ButtonType.Contained"
          @click="finish"
        >
          {{ t('common.finish') }}
        </BfButton>
      </div>
    </template>
  </n-modal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/userStore'
import { useBikeStore } from '@/store/bikeStore'
import { useMeasurementStore } from '@/store/measurementStore'
import TitleBar from '@/components/layout/TitleBar.vue'
import BikeSetupFinished from '@/components/preview/BikeSetupFinished.vue'
import FinishedSetup from '@/components/setup/FinishedSetup.vue'
import { ButtonType, ButtonState, BfButton } from '@finetic/bikefitting-ui'
import { useI18n } from 'vue-i18n'
import { NModal } from 'naive-ui'
import { getConfig } from '@/config'

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()
const bikeStore = useBikeStore()
const measurementStore = useMeasurementStore()

const updated = ref(false)
const showModal = ref(false)

const previous = () => {
  router.push({ name: 'data' })
}

const next = () => {
  showModal.value = true
}

const finish = () => {
  showModal.value = false
  updated.value = true
}

const backToPortal = () => {
  const customerId = userStore.customerId
  const sessionId = userStore.sessionId
  const bikeNo = bikeStore.bikeNo

  const handlebarX = measurementStore.latestMeasurement.handlebar.x
  const handlebarY = measurementStore.latestMeasurement.handlebar.y
  const saddleX = measurementStore.latestMeasurement.saddle.x
  const saddleY = measurementStore.latestMeasurement.saddle.y

  const redirectUrl = `${getConfig('VUE_APP_PORTAL_URL')}/BikeSetup/ReverseCalculation/${customerId}?handlebarX=${handlebarX}&handlebarY=${handlebarY}&saddleX=${saddleX}&saddleY=${saddleY}&bikeNo=${bikeNo}&sessionId=${sessionId}`

  userStore.logout()

  location.href = redirectUrl
}
</script>

<style lang="scss">
.completion-view {
  background-color: #fbfbfb;
  display: flex;
  height: 100%;

  &--updated {
    justify-content: center;
    align-items: center;
    padding: 2.4rem;
    text-align: center;
  }

  &__illustration {
    flex: 1 0 40%;
    background-color: #f5f5f5;
    padding: 2.4rem;
    overflow-y: auto;
  }

  &__content {
    flex: 1 0 60%;
    background-color: #fbfbfb;
    padding: 2.4rem;
    overflow-y: auto;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.8rem;
  }
}
</style>
